import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../lib/auth";
import { formatApiErrorDetail } from "../../lib/api";

const DEMO = [
  { role: "Admin", email: "vishaljgupta@gmail.com", pass: "Admin@123" },
  { role: "Staff", email: "staff@loandoctor.in", pass: "Staff@123" },
  { role: "Agent", email: "agent@loandoctor.in", pass: "Agent@123" },
  { role: "Affiliate", email: "affiliate@loandoctor.in", pass: "Affiliate@123" },
  { role: "Customer", email: "customer@loandoctor.in", pass: "Customer@123" },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(location.state?.from || "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const quick = (d) => { setEmail(d.email); setPassword(d.pass); };
  const field = "bg-white border-cream-300 h-11 text-soil-700 placeholder:text-soil-400";

  return (
    <div className="min-h-screen bg-cream-100 bharat-pattern flex items-center justify-center p-4" data-testid="login-page">
      <div className="absolute top-6 left-6"><Logo onLight /></div>
      <div className="w-full max-w-md">
        <div className="card-cream rounded-2xl p-8">
          <h1 className="font-heading text-3xl font-extrabold text-forest-800">Welcome Back to Genesis</h1>
          <p className="text-soil-500 text-sm mt-1">Access your Genesis Group account.</p>
          <form onSubmit={submit} className="space-y-4 mt-6" data-testid="login-form">
            <div>
              <Label className="text-soil-600 text-sm mb-1.5 block">Email</Label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={field} />
            </div>
            <div>
              <Label className="text-soil-600 text-sm mb-1.5 block">Password</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={field} />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full forest-gradient text-cream-50 font-bold h-12 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <div className="mt-2">
            <div className="flex items-center gap-3 my-4"><div className="h-px bg-cream-300 flex-1" /><span className="text-soil-400 text-xs">OR</span><div className="h-px bg-cream-300 flex-1" /></div>
            <button type="button" onClick={googleLogin} data-testid="google-login-button" className="w-full flex items-center justify-center gap-3 h-12 rounded-full bg-white border border-cream-300 text-soil-700 font-semibold hover:bg-cream-50 transition-colors">
              <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 via-red-500 to-yellow-400 flex items-center justify-center text-white text-[11px] font-bold">G</span>
              Continue with Google
            </button>
          </div>
          <p className="text-soil-500 text-sm mt-5 text-center">New here? <Link to="/register" className="text-terracotta-600 font-semibold">Create an account</Link></p>
        </div>

        <div className="card-cream rounded-2xl p-5 mt-4">
          <div className="text-xs uppercase tracking-widest text-terracotta-600 font-semibold mb-3">Demo Accounts — tap to autofill</div>
          <div className="grid grid-cols-2 gap-2">
            {DEMO.map((d) => (
              <button key={d.role} onClick={() => quick(d)} data-testid={`demo-${d.role.toLowerCase()}`} className="text-left rounded-lg border border-cream-300 p-2.5 hover:border-terracotta-500/50 transition-colors">
                <div className="text-forest-800 text-sm font-semibold">{d.role}</div>
                <div className="text-soil-400 text-[11px] truncate">{d.email}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="card-cream rounded-2xl p-5 mt-4 text-center">
          <h2 className="font-heading text-lg font-semibold text-forest-800">Sign in to the Genesis Group ecosystem</h2>
          <p className="text-soil-500 text-sm mt-2">
            Your Genesis account is a single secure login for the entire All-in-One Business Center. Members, customers,
            agents, affiliates and staff use this portal to manage their journey across ten business verticals — from Genesis
            Finance (Loan Doctor) and Agro-Dairy to E-commerce, Green Energy, Healthcare, Education and IT/VFX.
          </p>
          <p className="text-soil-500 text-sm mt-2">
            After signing in you can track your team tree and downline, view ranks and rewards, request payouts and
            withdrawals, upload and share documents securely, follow your leads, and receive real-time notifications.
            Rooted in Bharat and connected to opportunity, Genesis Group brings business, shopping, affiliate growth and
            community together in one trusted place. New to Genesis? Create a free account to get started in minutes.
          </p>
        </div>
      </div>
    </div>
  );
}
